import dayjs from "dayjs";
import apiClient from "./baseService";
import {handleAxiosError} from "./axiosErrorHandler";
import {FieldResult, FieldResultLayersProps, FieldResultStatistics} from "../types/domain/fieldResult";
import {ResultImage} from "../types/domain/resultImage";
import {FieldHistoryDaysType} from "../types/fieldHistoryDays";
import {ExportFileFormatType} from "../types/domain/exportFileFormatType";
import {DateFormatType} from "../types/domain/dateFormat";

export const getCompanyResultsDates = async (companyId: string, farmId?: string, fieldId?: string) => {
    let query = "";
    if (farmId) {
        query = `?farmId=${farmId}`;
    } else if (fieldId) {
        query = `?fieldId=${fieldId}`;
    }
    return apiClient
        .get<Promise<string[]>>(`/companies/${companyId}/results-dates${query}`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getCompanyResultsSignedUrl = async (companyId: string) => {
    return apiClient
        .get<Promise<string>>(`/companies/${companyId}/results-signed-url`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getCompanyResultsStatistics = async (companyId: string, date: string) => {
    return apiClient
        .get<Record<string, FieldResultStatistics>>(`/companies/${companyId}/results/${date}/statistics`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getCompanyResultsLayers = async (companyId: string, date: string) => {
    return apiClient
        .get<Record<string, FieldResultLayersProps>>(`/companies/${companyId}/results/${date}/layers`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getFieldResults = async (
    companyId: string,
    fieldId: string,
    date: dayjs.Dayjs,
    range: FieldHistoryDaysType,
): Promise<FieldResult[]> => {
    return apiClient
        .get<FieldResult[]>(`/companies/${companyId}/fields/${fieldId}/results/${date.format("YYYY-MM-DD")}`, {
            params: {range},
        })
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getFarmResultsImages = async (props: {
    companyId: string;
    farmId: string;
    date: string;
    layer: string;
}): Promise<ResultImage[]> => {
    const {companyId, farmId, date, layer} = props;

    return apiClient
        .get<ResultImage[]>(`/companies/${companyId}/farms/${farmId}/images`, {params: {date, layer}})
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getResultsDownloads = async (props: {
    companyId: string;
    farmIds?: (string | undefined)[];
    fieldId?: string;
    periodStart?: string;
    periodEnd?: string;
    format: ExportFileFormatType;
    dateFormat: DateFormatType;
    latestDay?: boolean;
}): Promise<Blob> => {
    const {companyId, fieldId, farmIds, periodStart, periodEnd, format, dateFormat, latestDay} = props;
    const queryParams: Record<string, string | undefined | (undefined | string)[] | [] | boolean> = {
        farmIds,
        fieldId,
        periodStart,
        periodEnd,
        format,
        dateFormat,
        latestDay,
    };

    return await apiClient
        .get<Blob>(`/companies/${companyId}/results-export`, {
            params: queryParams,
            responseType: "blob",
        })
        .then((response) => response.data)
        .catch(handleAxiosError);
};
